import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notificationsAPI } from '../api/client'
import { IconBell } from './AppNavIcons'
import NotifKindIcon from './NotifKindIcon'

interface Notification {
  id: number
  kind: string
  title: string
  body: string | null
  link_listing_id: number | null
  link_offer_id: number | null
  is_read: boolean
  created_at: string
}

interface NotificationsPageProps {
  token: string | null
  onOpenOffer?: (offerId: number, role: 'seller' | 'buyer') => void
  onOpenMyOffers?: () => void
  onOpenListing?: (listingId: number) => void
  onUnreadChange?: (count: number) => void
}

function kindTone(kind: string): string {
  if (kind.startsWith('exchange')) return 'exchange'
  if (kind === 'new_offer' || kind === 'offer_message') return 'offer'
  if (kind === 'offer_accepted') return 'success'
  if (kind === 'offer_rejected' || kind === 'offer_superseded') return 'muted'
  if (kind === 'order_shipped' || kind === 'delivery_confirmed' || kind === 'exchange_send_to_warehouse') return 'ship'
  if (kind === 'transaction_cancelled') return 'muted'
  return 'default'
}

function formatNotifDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

export default function NotificationsPage({
  token,
  onOpenOffer,
  onOpenMyOffers,
  onOpenListing,
  onUnreadChange,
}: NotificationsPageProps) {
  const { t } = useTranslation()
  const [items, setItems] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)

  const refreshUnread = useCallback(
    (list: Notification[]) => {
      const unread = list.filter((n) => !n.is_read).length
      onUnreadChange?.(unread)
      window.dispatchEvent(new CustomEvent('redevicer:notifications-updated', { detail: { unread } }))
    },
    [onUnreadChange],
  )

  const load = useCallback(() => {
    if (!token) return
    setLoading(true)
    notificationsAPI
      .list(token)
      .then((res) => {
        const list = res.data as Notification[]
        setItems(list)
        refreshUnread(list)
      })
      .catch(() => {
        setItems([])
        refreshUnread([])
      })
      .finally(() => setLoading(false))
  }, [token, refreshUnread])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!token) return
    const poll = window.setInterval(load, 12000)
    const onVis = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.clearInterval(poll)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [token, load])

  function markAllRead() {
    if (!token) return
    notificationsAPI.markAllRead(token).then(() => load())
  }

  function openNotification(n: Notification) {
    if (!token) return
    const mark = n.is_read
      ? Promise.resolve()
      : notificationsAPI.markOneRead(n.id, token)

    mark.then(() => {
      if (
        (n.kind === 'new_offer' || n.kind === 'exchange_offer' || n.kind === 'offer_message') &&
        n.link_offer_id
      ) {
        const role = n.kind === 'offer_message' && n.title.startsWith('Pardavėjo') ? 'buyer' : 'seller'
        onOpenOffer?.(n.link_offer_id, n.kind === 'offer_message' ? role : 'seller')
      } else if (
        n.kind === 'offer_accepted' ||
        n.kind === 'offer_rejected' ||
        n.kind === 'offer_superseded' ||
        n.kind === 'order_shipped' ||
        n.kind === 'delivery_confirmed' ||
        n.kind === 'exchange_send_to_warehouse' ||
        n.kind === 'exchange_direct' ||
        n.kind === 'transaction_cancelled'
      ) {
        onOpenMyOffers?.()
      } else if (n.link_listing_id) {
        onOpenListing?.(n.link_listing_id)
      }
      load()
    })
  }

  const hasUnread = items.some((n) => !n.is_read)

  return (
    <section className="vc-notifications-page vc-notifications-page--modern">
      {items.length > 0 && (
        <header className="vc-notif-page__hero">
          <div className="vc-notif-page__hero-icon" aria-hidden="true">
            <IconBell />
          </div>
          <h2>{t('vc.notif.title')}</h2>
          <p className="vc-notif-page__hint">{t('vc.notif.intro')}</p>
          <div className="vc-notif-page__hero-actions">
            <button type="button" className="vc-notif-refresh" onClick={load} disabled={loading}>
              {loading ? '…' : t('catalog.refresh')}
            </button>
            {hasUnread && (
              <button type="button" className="vc-notif-mark-all" onClick={markAllRead}>
                {t('vc.notif.markAllRead')}
              </button>
            )}
          </div>
        </header>
      )}

      {loading && items.length === 0 && (
        <div className="vc-notif-empty vc-notif-empty--bloom vc-notif-empty--loading">
          <div className="vc-notif-empty__glow" aria-hidden="true" />
          <div className="vc-notif-empty__icon" aria-hidden="true">
            <IconBell />
          </div>
          <p className="vc-notif-empty__title">{t('common.loading')}</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="vc-notif-empty vc-notif-empty--bloom">
          <div className="vc-notif-empty__glow" aria-hidden="true" />
          <div className="vc-notif-empty__icon" aria-hidden="true">
            <IconBell />
          </div>
          <p className="vc-notif-empty__title">{t('vc.notif.empty')}</p>
          <p className="vc-notif-empty__hint">{t('vc.notif.intro')}</p>
        </div>
      )}

      {items.length > 0 && (
        <ul className="vc-notif-list vc-notif-list--modern">
          {items.map((n, idx) => (
            <li key={n.id} style={{ animationDelay: `${0.08 + idx * 0.05}s` }}>
              <button
                type="button"
                className={`vc-notif-card vc-notif-card--modern vc-notif-card--${kindTone(n.kind)}${n.is_read ? '' : ' vc-notif-card--unread'}`}
                onClick={() => openNotification(n)}
              >
                <span className="vc-notif-card__icon" aria-hidden="true">
                  <NotifKindIcon kind={n.kind} />
                </span>
                <span className="vc-notif-card__body">
                  <span className="vc-notif-card__meta">
                    {!n.is_read && <span className="vc-notif-card__dot" aria-hidden="true" />}
                    <time className="vc-notif-card__date" dateTime={n.created_at}>
                      {formatNotifDate(n.created_at)}
                    </time>
                  </span>
                  <span className="vc-notif-card__title">{n.title}</span>
                  {n.body && (
                    <span className="vc-notif-card__text">{n.body.replace(/\n/g, ' · ')}</span>
                  )}
                  <span className="vc-notif-card__cta">{t('vc.notif.tapToOpen')}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <p className="vc-notif-footnote vc-notif-footnote--modern">{t('vc.notif.footnote')}</p>
      )}
    </section>
  )
}
